import React, { useState, useEffect } from 'react';
import { TelegramBot, V8Login } from '../../types/admin';
import { telegramBotsApi } from '../../utils/admin/telegramApi';
import './AdminStyles.css';

interface TelegramBotFormProps {
  bot?: TelegramBot;
  onSubmit: (data: any) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

// Preserva o '+' inicial (formato internacional, ex.: +5547999940358) e mantém só dígitos no resto.
// O número de suporte aparece como texto nas mensagens — o '+' faz o WhatsApp/Telegram auto-linkarem.
const normalizeSupportNumber = (value: string): string => {
  const trimmed = value.trim();
  const hasPlus = trimmed.startsWith('+');
  const digits = trimmed.replace(/\D/g, '');
  return digits ? `${hasPlus ? '+' : ''}${digits}` : '';
};

// Aceita opcionalmente o '+' seguido de 10-15 dígitos.
const isValidSupportNumber = (value: string): boolean => {
  const digits = value.replace(/\D/g, '');
  return digits.length >= 10 && digits.length <= 15;
};

// Formulário dedicado dos bots do Telegram. Bem mais simples que os outros canais: só o token
// do @BotFather + o V8. O backend descobre bot_id/username (getMe) e configura o webhook sozinho.
export const TelegramBotForm: React.FC<TelegramBotFormProps> = ({
  bot,
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const isEditing = !!bot;

  const [formData, setFormData] = useState({
    bot_token: '',
    v8_id: bot?.v8_id ? String(bot.v8_id) : '',
    support_number: bot?.support_number || '',
    support_number_premium: bot?.support_number_premium || '',
    proxy_url: bot?.proxy_url || '',
    is_active: bot?.is_active ?? true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showSecret, setShowSecret] = useState(false);
  const [v8Logins, setV8Logins] = useState<V8Login[]>([]);

  // Carrega os V8 logins disponíveis para o select (o bot precisa de um V8 p/ consulta/proposta FGTS).
  useEffect(() => {
    let active = true;
    telegramBotsApi.listV8Logins()
      .then(list => { if (active) setV8Logins(list); })
      .catch(() => { if (active) setV8Logins([]); });
    return () => { active = false; };
  }, []);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Token do BotFather: <bot_id>:<35+ chars>. Obrigatório no create; no update, só se preenchido.
    const tokenRegex = /^\d{5,}:[A-Za-z0-9_-]{30,}$/;
    if (!isEditing) {
      if (!formData.bot_token.trim()) {
        newErrors.bot_token = 'Token do bot é obrigatório';
      } else if (!tokenRegex.test(formData.bot_token.trim())) {
        newErrors.bot_token = 'Token inválido (formato do @BotFather)';
      }
    } else if (formData.bot_token.trim() && !tokenRegex.test(formData.bot_token.trim())) {
      newErrors.bot_token = 'Token inválido (formato do @BotFather)';
    }

    if (!formData.v8_id) {
      newErrors.v8_id = 'Selecione um V8 para atrelar ao bot';
    }

    const supportFields: Array<'support_number' | 'support_number_premium'> = ['support_number', 'support_number_premium'];
    for (const f of supportFields) {
      const v = formData[f];
      if (v && v.trim() && !isValidSupportNumber(v)) {
        newErrors[f] = 'Número inválido (10-15 dígitos, com ou sem +)';
      }
    }

    if (formData.proxy_url && formData.proxy_url.trim()) {
      const candidate = formData.proxy_url.startsWith('http') ? formData.proxy_url : `https://${formData.proxy_url}`;
      try {
        new URL(candidate);
      } catch {
        newErrors.proxy_url = 'URL do proxy inválida';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type: inputType } = e.target;
    const finalValue = inputType === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    setFormData(prev => ({ ...prev, [name]: finalValue }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Monta o payload conforme criação/edição (só o que o backend aceita).
  const buildPayload = () => {
    if (!isEditing) {
      return {
        bot_token: formData.bot_token.trim(),
        v8_id: Number(formData.v8_id),
        support_number: formData.support_number.trim() ? normalizeSupportNumber(formData.support_number) : undefined,
        support_number_premium: formData.support_number_premium.trim() ? normalizeSupportNumber(formData.support_number_premium) : undefined,
        proxy_url: formData.proxy_url.trim() || undefined,
        is_active: formData.is_active,
      };
    }
    const payload: any = {
      is_active: formData.is_active,
      proxy_url: formData.proxy_url.trim() ? formData.proxy_url.trim() : null,
      support_number: formData.support_number.trim() ? normalizeSupportNumber(formData.support_number) : null,
      support_number_premium: formData.support_number_premium.trim() ? normalizeSupportNumber(formData.support_number_premium) : null,
    };
    if (formData.bot_token.trim()) payload.bot_token = formData.bot_token.trim();
    if (formData.v8_id) payload.v8_id = Number(formData.v8_id);
    return payload;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      await onSubmit(buildPayload());
    } catch (error) {
      console.error('Erro ao salvar bot do Telegram:', error);
    }
  };

  return (
    <form className="account-form" onSubmit={handleSubmit}>
      <h3>{bot ? 'Editar Bot' : 'Adicionar Bot'} Telegram</h3>

      {isEditing && (
        <div className="form-group">
          <label>Bot</label>
          <input type="text" value={`@${bot!.bot_username} (id ${bot!.bot_id})`} disabled readOnly />
        </div>
      )}

      <div className="form-group">
        <label htmlFor="bot_token">Token do Bot (@BotFather) {isEditing ? '(deixe em branco para manter)' : '*'}</label>
        <input
          id="bot_token"
          type={showSecret ? 'text' : 'password'}
          name="bot_token"
          value={formData.bot_token}
          onChange={handleChange}
          placeholder={isEditing ? '••••••••' : 'Ex: 123456789:AA...'}
          disabled={loading}
        />
        {errors.bot_token && <span className="error">{errors.bot_token}</span>}
      </div>

      <div className="form-group checkbox">
        <input
          id="showSecret"
          type="checkbox"
          checked={showSecret}
          onChange={(e) => setShowSecret(e.target.checked)}
          disabled={loading}
        />
        <label htmlFor="showSecret">Mostrar token</label>
      </div>

      <div className="form-group">
        <label htmlFor="v8_id">V8 atrelado ao bot *</label>
        <select
          id="v8_id"
          name="v8_id"
          value={formData.v8_id}
          onChange={handleChange}
          disabled={loading}
        >
          <option value="">— Selecione um V8 —</option>
          {v8Logins.map(v8 => (
            <option key={v8.id} value={String(v8.id)}>{v8.login} (#{v8.id})</option>
          ))}
        </select>
        {errors.v8_id && <span className="error">{errors.v8_id}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="support_number">Número de Suporte (Opcional)</label>
        <input
          id="support_number"
          type="tel"
          name="support_number"
          value={formData.support_number}
          onChange={handleChange}
          placeholder="Ex: +5547999940358"
          disabled={loading}
          maxLength={16}
        />
        {errors.support_number && <span className="error">{errors.support_number}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="support_number_premium">Número de Suporte Premium (Opcional)</label>
        <input
          id="support_number_premium"
          type="tel"
          name="support_number_premium"
          value={formData.support_number_premium}
          onChange={handleChange}
          placeholder="Ex: +5547999940358"
          disabled={loading}
          maxLength={16}
        />
        {errors.support_number_premium && <span className="error">{errors.support_number_premium}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="proxy_url">URL do Proxy (Opcional)</label>
        <input
          id="proxy_url"
          type="text"
          name="proxy_url"
          value={formData.proxy_url}
          onChange={handleChange}
          placeholder="Ex: proxy.example.com:8080"
          disabled={loading}
          maxLength={1000}
        />
        {errors.proxy_url && <span className="error">{errors.proxy_url}</span>}
      </div>

      <div className="form-group checkbox">
        <input
          id="is_active"
          type="checkbox"
          name="is_active"
          checked={formData.is_active}
          onChange={handleChange}
          disabled={loading}
        />
        <label htmlFor="is_active">Ativo</label>
      </div>

      <div className="form-actions">
        <button type="submit" className="btn-primary" disabled={loading}>
          {loading ? 'Salvando...' : (bot ? 'Atualizar' : 'Criar')}
        </button>
        <button type="button" className="btn-secondary" onClick={onCancel} disabled={loading}>
          Cancelar
        </button>
      </div>
    </form>
  );
};
